/**
 * Lokale identiteit koppelen aan het serverprofiel.
 *
 * ─── HET PROBLEEM ────────────────────────────────────────────────────────────
 *
 * Een account heeft op dit apparaat twee mogelijke identiteiten:
 *
 *   1. een LOKAAL id, toegekend toen het account op dit toestel werd
 *      aangemaakt (of door een leerkracht in de klas werd toegevoegd);
 *   2. het SERVER id (`profiles.id`), dat pas bekend is na het inloggen.
 *
 * Vrijwel alle per-account data op dit apparaat hangt aan het id: de gekozen
 * Djay-look, de ontdekte easter eggs, oefenvoortgang, tips. Zodra iemand
 * inlogt, zet `applyProfileToAccounts` het actieve account op het SERVER id —
 * en dan wijst alles wat onder het lokale id stond nergens meer naar.
 *
 * Het zichtbare gevolg is stil en verwarrend: een kind logt in en ziet zijn
 * mascotte terug op de standaardlook. Er is geen foutmelding, want er is
 * technisch niets misgegaan; er wordt alleen op de verkeerde plek gekeken.
 * Voor een kind van tien leest dat als "mijn spullen zijn weg".
 *
 * ─── DE OPLOSSING ────────────────────────────────────────────────────────────
 *
 * Bij het inloggen zoeken we het lokale account dat dezelfde persoon is, en
 * kopiëren we zijn opgeslagen gegevens naar het server-id.
 *
 * Drie regels die deze operatie veilig houden:
 *
 *   • KOPIËREN, niet verplaatsen. Blijkt de koppeling ooit verkeerd, dan staat
 *     het origineel er nog. Dataverlies is hier duurder dan een dubbele sleutel.
 *   • NOOIT OVERSCHRIJVEN. Staat er onder het server-id al iets, dan is dat
 *     nieuwer of bewuster gekozen en wint het.
 *   • Alleen bij een EENDUIDIGE match. Twee lokale accounts met dezelfde
 *     gebruikersnaam is geen match maar een gok, en op een gedeeld
 *     gezinsapparaat is die gok de spullen van een broertje.
 */

import { info as logInfo, error as logError } from '../../utils/logger';

/** Minimale vorm van de accountstaat, zonder het accounts-domein mee te trekken. */
interface AccountsShape {
  accountsById: Record<string, Record<string, unknown>>;
  activeAccountId: string | null;
}

/** Waar het serverprofiel op gematcht wordt. */
interface ProfileIdentity {
  id: string;
  username: string | null;
  email?: string | null;
}

function readString(source: Record<string, unknown>, key: string): string | null {
  const value = source[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Zoek het lokale account dat dezelfde persoon is als het ingelogde profiel.
 *
 * De sleutel is de GEBRUIKERSNAAM, niet de weergavenaam. Een weergavenaam is
 * vrije tekst en niet uniek — twee kinderen die allebei "Sam" heten zijn geen
 * uitzondering maar de norm. De gebruikersnaam is waarmee je inlogt, dus als
 * die overeenkomt is het per definitie hetzelfde account.
 *
 * Voor volwassenen geldt hetzelfde met het e-mailadres.
 *
 * Retourneert `null` bij géén match én bij MEERDERE matches. Dat tweede is
 * bewust: een dubbele treffer betekent dat de gebruikersnaam op dit apparaat
 * niet uniek is, en dan is elke keuze een gok.
 */
export function findLocalTwinId(accounts: AccountsShape, profile: ProfileIdentity): string | null {
  const username = profile.username?.toLowerCase() ?? null;
  const email = profile.email?.toLowerCase() ?? null;
  if (!username && !email) return null;

  const matches: string[] = [];
  for (const [id, account] of Object.entries(accounts.accountsById)) {
    // Het server-id is geen "lokale tweeling" van zichzelf.
    if (id === profile.id) continue;
    const localUsername = readString(account, 'username')?.toLowerCase() ?? null;
    const localEmail = readString(account, 'email')?.toLowerCase() ?? null;
    const sameUser = !!username && localUsername === username;
    const sameEmail = !!email && localEmail === email;
    if (sameUser || sameEmail) matches.push(id);
  }

  if (matches.length !== 1) return null;
  return matches[0];
}

/**
 * Kopieer alle localStorage-sleutels van het oude id naar het nieuwe.
 *
 * Bewust GENERIEK op basis van het id in de sleutelnaam, en niet een lijstje
 * bekende prefixen. Een lijst is op het moment van schrijven compleet en raakt
 * daarna achter zodra iemand een nieuw per-account domein toevoegt — en dat
 * verlies is precies zo onzichtbaar als het verlies dat we hier repareren.
 *
 * Retourneert het aantal gekopieerde sleutels, zodat de aanroeper kan loggen
 * of er daadwerkelijk iets is gebeurd.
 */
export function rebaseLocalAccountData(oldId: string, newId: string): number {
  if (!oldId || !newId || oldId === newId) return 0;
  try {
    // Eerst inventariseren, dan pas schrijven. Tijdens het schrijven groeit
    // localStorage, en dan zou een lopende index-lus zijn eigen resultaten
    // opnieuw tegenkomen.
    const sourceKeys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.includes(oldId)) sourceKeys.push(key);
    }

    let copied = 0;
    for (const key of sourceKeys) {
      const targetKey = key.split(oldId).join(newId);
      // Nooit overschrijven: bestaande data onder het server-id is leidend.
      if (localStorage.getItem(targetKey) !== null) continue;
      const value = localStorage.getItem(key);
      if (value === null) continue;
      localStorage.setItem(targetKey, value);
      copied += 1;
    }
    return copied;
  } catch (err) {
    // Een volle of geblokkeerde localStorage mag het inloggen niet breken.
    // De gebruiker ziet dan zijn oude look niet terug — vervelend, maar dat is
    // exact de situatie van vóór deze functie.
    logError('[IdentityMerge] Lokale gegevens overzetten mislukt', err);
    return 0;
  }
}

/**
 * Zoek de lokale tweeling en zet zijn gegevens over naar het server-id.
 *
 * Idempotent en zelf-stoppend: na de eerste keer bestaat de data onder het
 * server-id, en de "nooit overschrijven"-regel maakt elke volgende aanroep een
 * lege operatie.
 *
 * Het lokale account zelf blijft in de lijst staan. Dat is een bewuste grens:
 * het samenvoegen van twee accounts raakt ook de relaties (welke ouder hoort
 * bij welk kind), en een half uitgevoerde samenvoeging is erger dan twee
 * losse accounts. Wat hier gerepareerd wordt, is dat de spullen van het kind
 * meeverhuizen — niet de accountadministratie.
 */
export function mergeLocalIdentityIntoProfile(accounts: AccountsShape, profile: ProfileIdentity): void {
  const twinId = findLocalTwinId(accounts, profile);
  if (!twinId) return;
  const copied = rebaseLocalAccountData(twinId, profile.id);
  if (copied > 0) {
    // Geen id's in de log: dat is een accountidentificator, en die hoort niet
    // in de console van een gedeeld apparaat.
    logInfo(`[IdentityMerge] ${copied} lokale instelling(en) overgezet naar het serveraccount`);
  }
}
